import { useQuery } from 'react-query';
import { predictionWsAxiosApi } from 'src/Config/axiosApi';
import { PredictionOpportunity } from '../types';

interface OpportunitiesSnapshotResponse {
  data: PredictionOpportunity[];
  count: number;
  timestamp: number;
}

/**
 * Seeds the Opportunities table from the prediction-websocket REST endpoint.
 *
 * Why this exists: the WS `prediction_snapshot` event the gateway emits
 * inside `handleConnection` arrives before `socket.io-react-hook` has a
 * chance to register the React-side listener (`useSocketEvent` installs
 * it in a `useEffect`, which runs after the socket already connected and
 * the snapshot already flew past). The frontend would then sit at zero
 * rows forever — the live `prediction_update`/`prediction_delta` stream
 * merges into the cache but on a fresh load there is nothing to merge
 * into.
 *
 * Fetching the snapshot over REST removes the race entirely. The WS
 * remains the source of truth for live updates; this query just gives
 * the page a non-empty starting cache.
 */
export function useOpportunitiesSnapshot(enabled: boolean) {
  return useQuery<OpportunitiesSnapshotResponse>(
    ['prediction-opportunities-snapshot'],
    async () => {
      const { data } =
        await predictionWsAxiosApi.get<OpportunitiesSnapshotResponse>(
          '/predictions/opportunities',
        );
      return data;
    },
    {
      enabled,
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false,
    },
  );
}
